/**
 * Componente de linha para exibição de fluxo em formato lista
 * Utilizado na visualização alternativa à grade de cards
 * 
 * PERMISSÕES GRANULARES:
 * - canEditFlow: editar nome, duplicar, configurar gatilhos
 * - canDeleteFlow: excluir fluxo
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Play,
  Pause,
  Clock,
  Archive,
  MoreVertical,
  Eye,
  Pencil,
  Settings,
  Copy,
  Trash2,
  Boxes,
  Webhook,
  Download,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { Tables } from '@/integrations/supabase/types';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';

/**
 * Configuração visual por status do fluxo
 * Padrão alinhado à listagem de Agentes/Departamentos: Badge outline + StatusDot
 */
const statusConfig: Record<string, {
  label: string;
  icon: typeof Play;
  tone: 'emerald' | 'amber' | 'muted';
}> = {
  draft: {
    label: 'Rascunho',
    icon: Clock,
    tone: 'muted',
  },
  active: {
    label: 'Ativo',
    icon: Play,
    tone: 'emerald',
  },
  paused: {
    label: 'Pausado',
    icon: Pause,
    tone: 'amber',
  },
  archived: {
    label: 'Arquivado',
    icon: Archive,
    tone: 'muted',
  },
};

interface FlowListRowProps {
  flow: Tables<'flows'>;
  onEdit: (flow: Tables<'flows'>) => void;
  onDuplicate: (flow: Tables<'flows'>) => void;
  onDelete: (flow: Tables<'flows'>) => void;
  onSettings: (flow: Tables<'flows'>) => void;
  /** Callback para abrir modal de webhook */
  onWebhookConfig?: (flow: Tables<'flows'>) => void;
  /** Callback para exportar fluxo */
  onExport?: (flow: Tables<'flows'>) => void;
  /** Permissão para editar fluxos */
  canEditFlow?: boolean;
  /** Permissão para excluir fluxos */
  canDeleteFlow?: boolean;
}

/**
 * Verifica se fluxo possui gatilho webhook
 */
function hasWebhookTrigger(flow: Tables<'flows'>): boolean {
  const triggers = flow.trigger_config as unknown as Array<{ type: string }> | null;
  if (!Array.isArray(triggers)) return false;
  return triggers.some(t => t.type === 'webhook');
}

export function FlowListRow({ 
  flow, 
  onEdit, 
  onDuplicate, 
  onDelete, 
  onSettings,
  onWebhookConfig,
  onExport,
  canEditFlow,
  canDeleteFlow,
}: FlowListRowProps) {
  const navigate = useNavigate();
  const config = statusConfig[flow.status] || statusConfig.draft;
  const StatusIcon = config.icon;

  // Conta o número de nós do fluxo
  const nodeCount = useMemo(() => {
    const nodes = flow.nodes as unknown as Array<unknown>;
    return Array.isArray(nodes) ? nodes.length : 0;
  }, [flow.nodes]);

  return (
    <div
      className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => navigate(`/tenant/flows/${flow.id}`)}
    >
      {/* Ícone de status */}
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/50 shrink-0">
        <StatusIcon className="h-5 w-5 text-muted-foreground" />
      </div>

      {/* Nome, código identificador e descrição */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="font-medium truncate">{flow.name}</p>
          {/* Código identificador: 8 primeiros chars do UUID em uppercase.
              Selecionável (select-text) e bloqueia clique/mousedown para não abrir o fluxo. */}
          <code
            className="hidden sm:inline-flex shrink-0 px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono font-semibold text-muted-foreground tracking-wider select-text cursor-text"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {flow.id.substring(0, 8).toUpperCase()}
          </code>
        </div>
        <p className="text-sm text-muted-foreground truncate">
          {flow.description || 'Sem descrição'}
        </p>
      </div>

      {/* Status — padrão Badge outline + StatusDot (igual Agentes/Departamentos) */}
      <Badge
        variant="outline"
        className="shrink-0 h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
      >
        <StatusDot tone={config.tone} />
        {config.label}
      </Badge>

      {/* Contador de blocos */}
      <div className="hidden md:flex items-center gap-1.5 text-sm text-muted-foreground shrink-0 w-24">
        <Boxes className="h-4 w-4" />
        <span>{nodeCount} {nodeCount === 1 ? 'bloco' : 'blocos'}</span>
      </div>

      {/* Data de criação */}
      <div className="hidden lg:flex items-center gap-1.5 text-sm text-muted-foreground shrink-0 w-24">
        <Clock className="h-4 w-4" />
        <span>{format(new Date(flow.created_at), "dd/MM/yy", { locale: ptBR })}</span>
      </div>

      {/* Menu de ações */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {/* Visualizar - sempre disponível */}
          <DropdownMenuItem onClick={() => navigate(`/tenant/flows/${flow.id}`)}>
            <Eye className="h-4 w-4 mr-2" />
            Visualizar
          </DropdownMenuItem>
          
          {/* Exportar - sempre disponível para visualização */}
          {onExport && (
            <DropdownMenuItem onClick={() => onExport(flow)}>
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </DropdownMenuItem>
          )}
          
          {/* Ações de edição - requerem permissão */}
          {canEditFlow && (
            <>
              <DropdownMenuItem onClick={() => onEdit(flow)}>
                <Pencil className="h-4 w-4 mr-2" />
                Editar nome
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onSettings(flow)}>
                <Settings className="h-4 w-4 mr-2" />
                Configurar gatilhos
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(flow)}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicar
              </DropdownMenuItem>
              
              {/* Webhook de Integração - somente se fluxo tem gatilho webhook */}
              {hasWebhookTrigger(flow) && onWebhookConfig && (
                <DropdownMenuItem onClick={() => onWebhookConfig(flow)}>
                  <Webhook className="h-4 w-4 mr-2" />
                  Webhook de Integração
                </DropdownMenuItem>
              )}
            </>
          )}
          
          {/* Excluir - requer permissão de delete */}
          {canDeleteFlow && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => onDelete(flow)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
